import * as React from "react";
import {
  RefreshCw,
  AlertCircle,
  Activity,
  CheckCheck,
  Loader2,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { AppLayout } from "../../components/ui-v2/layout/AppLayout";
import { Card, CardContent } from "../../components/ui-v2/card";
import { Button } from "../../components/ui-v2/button";
import { Skeleton } from "../../components/ui-v2/skeleton";
import { EmptyState } from "../../components/ui-v2/empty-state";
import { FilterPill, FilterPillGroup } from "../../components/ui-v2/filter-pill";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui-v2/table";
import { Badge } from "../../components/ui-v2/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui-v2/tooltip";
import { Heading, Text, MonoText } from "../../components/ui-v2/typography";
import { useEvents } from "../../hooks/useEvents";

/**
 * V2 Events Page — Institutional Console redesign (issue #236,
 * DESIGN-SPEC.md §5 "Events": "filter pills + unread toggle -> log table
 * (Time | Event+severity | Category | Details)").
 *
 * Lives under the "Activity" top-level nav section (navRegistry.js
 * `subTabGroups.activity`); AppLayout/PageToolbar renders the
 * Feed/Realised gains/Dividends/Withdrawals/Contributions/Events(N) sub-tabs
 * automatically from the current route — this component only supplies the
 * page title ("Activity", matching Events.dc.html/Transactions.dc.html/
 * Contributions.dc.html, all of which show "Activity" as the 46px toolbar
 * title with the active sub-tab underlined) and the two toolbar actions
 * ("Mark all read" / "Refresh", Events.dc.html:31-32) via AppLayout's
 * secondaryActions slot.
 *
 * Category filtering is now real FilterPill buttons (§4.4) wired to
 * `useEvents().setFilters({ category })` — same hook/endpoint as the old
 * Select dropdown, just restyled per the mockup's pill row
 * (Events.dc.html:38-45). Unread-only is a toggle switch matching the
 * mockup's track-and-thumb control (Events.dc.html:46-49), wired to the same
 * `filters.unreadOnly` state as the old button.
 *
 * All prior functionality retained: category filter, unread-only filter,
 * mark all as read, manual refresh, pagination (Load More), relative +
 * absolute (tooltip) timestamps, unread dot + bold text, empty/loading/error
 * states, "Go to Dashboard" empty-state CTA. See session file for the full
 * per-screen feature audit and declared deviations (PORTFOLIO_REFRESHED
 * filtering/last-refresh-time display carried over verbatim from the old
 * table-based implementation).
 */

/**
 * Category options for filtering (mirrors the old Select's option list —
 * same `category` query param values consumed by useEvents/backend).
 */
const CATEGORY_OPTIONS = [
  { value: "all", label: "All categories" },
  { value: "auth", label: "Auth" },
  { value: "holding", label: "Holding" },
  { value: "portfolio", label: "Portfolio" },
  { value: "snapshot", label: "Snapshot" },
  { value: "contribution", label: "Cash flow" },
  { value: "analytics", label: "Analytics" },
  { value: "system", label: "System" },
];

/**
 * Severity -> 7x7 square color (DESIGN-SPEC §4.6 "Event rows: 7x7 severity
 * square (r2: success/info/warning/muted)"). Bound to theme tokens so both
 * console-dark and console-light resolve the correct value automatically
 * (Events.dc.html's hardcoded hex values are this same palette's DARK-theme
 * resolution: success #2bb596, info #3b9eff, warning #d9a13b, muted #41586f).
 */
const SEVERITY_SQUARE_CLASS = {
  success: "bg-success",
  info: "bg-accent",
  warning: "bg-warning",
  error: "bg-destructive",
  muted: "bg-chart-neutral-1",
};

/**
 * Format relative timestamp (e.g., "2m ago", "1h ago")
 */
const formatRelativeTime = (timestamp) => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffSec < 60) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return date.toLocaleDateString();
};

/**
 * Format full timestamp for tooltip
 */
const formatFullTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/**
 * Format event description
 */
const formatDescription = (event) => {
  if (event.description) return event.description;

  const descriptions = {
    LOGIN: "User logged in",
    LOGOUT: "User logged out",
    SESSION_EXPIRED: "Session expired",
    HOLDING_CREATED: `Created holding${event.metadata?.assetName ? `: ${event.metadata.assetName}` : ""}`,
    HOLDING_UPDATED: `Updated holding${event.metadata?.assetName ? `: ${event.metadata.assetName}` : ""}`,
    HOLDING_DELETED: `Deleted holding${event.metadata?.assetName ? `: ${event.metadata.assetName}` : ""}`,
    HOLDING_SOLD: `Sold ${event.metadata?.units || ""} units${event.metadata?.assetName ? ` of ${event.metadata.assetName}` : ""}`,
    PORTFOLIO_REFRESHED: "Portfolio prices refreshed",
    PRICE_UPDATE: "Portfolio prices updated",
    PORTFOLIO_EXPORTED: "Portfolio exported",
    SNAPSHOT_CREATED: "Portfolio snapshot created",
    SNAPSHOT_FAILED: "Snapshot creation failed",
    SNAPSHOT_DELETED: "Snapshot deleted",
    CONTRIBUTION_ADDED: `Added contribution${event.metadata?.amount ? `: ${event.metadata.amount} ${event.metadata.currency || ""}` : ""}`,
    CONTRIBUTION_UPDATED: "Updated contribution",
    CONTRIBUTION_DELETED: "Deleted contribution",
    WITHDRAWAL_ADDED: `Added withdrawal${event.metadata?.amount ? `: ${event.metadata.amount} ${event.metadata.currency || ""}` : ""}`,
    ANALYTICS_VIEWED: "Viewed analytics",
    REPORT_GENERATED: "Report generated",
    ERROR_OCCURRED: `Error: ${event.metadata?.error?.message || "An error occurred"}`,
    BACKUP_COMPLETED: "Backup completed",
    IMPORT_COMPLETED: "Import completed",
    EXPORT_COMPLETED: "Export completed",
  };

  return descriptions[event.eventType] || event.eventType.replace(/_/g, " ");
};

/**
 * Get category label from event type (drives both the Category column badge
 * and — reversed — the CATEGORY_OPTIONS filter pills).
 */
const getEventCategory = (eventType) => {
  if (eventType.startsWith("HOLDING")) return "Holding";
  if (eventType.startsWith("PORTFOLIO")) return "Portfolio";
  if (eventType.startsWith("SNAPSHOT")) return "Snapshot";
  if (eventType.startsWith("CONTRIBUTION") || eventType.startsWith("WITHDRAWAL")) return "Cash flow";
  if (eventType === "LOGIN" || eventType === "LOGOUT" || eventType === "SESSION_EXPIRED") return "Auth";
  return "System";
};

/**
 * Map an event's severity/eventType to the icon shown in the description
 * cell isn't part of the new compact table (mockup drops per-row icons in
 * favor of the severity square, Events.dc.html:69-70) — EVENT_ICONS is no
 * longer needed and intentionally not ported (declared deviation: the icon
 * was decorative and duplicated the severity square's signal; severity is
 * still conveyed, now via square color + text, never color-only per §7).
 */
const getEventDetails = (event) => {
  const details = [];
  if (event.metadata?.assetName) {
    details.push(event.metadata.ticker || event.metadata.assetName);
  }
  if (event.metadata?.units) {
    details.push(`${event.metadata.units} units`);
  }
  if (event.metadata?.amount) {
    details.push(`${event.metadata.amount} ${event.metadata.currency || ""}`);
  }
  if (event.metadata?.platform) {
    details.push(event.metadata.platform);
  }
  return details.length > 0 ? details.join(" · ") : "–";
};

/**
 * Loading skeleton — mirrors the restyled table shape (filter row + table
 * card) using the single-cycle Skeleton primitive (§6/§7: no shimmer >1
 * cycle, motion-reduce disables it — see skeleton.jsx).
 */
function LoadingState() {
  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex items-center gap-2">
        <Skeleton className="h-[26px] w-64" />
        <Skeleton className="h-[26px] w-32" />
      </div>
      <Card>
        <CardContent className="p-0">
          <div className="space-y-0">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={`row-skeleton-${i}`} className="flex items-center gap-4 border-b border-hairline px-[18px] py-2">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-3 w-48 flex-1" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-3 w-24" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Error state component
 */
function ErrorState({ error, onRetry }) {
  return (
    <Card>
      <CardContent className="py-16">
        <div className="flex flex-col items-center justify-center">
          <div className="rounded-full bg-destructive/10 p-4">
            <AlertCircle className="h-12 w-12 text-destructive" />
          </div>
          <Heading level={4} className="mt-4">
            Failed to load events
          </Heading>
          <Text variant="secondary" className="mt-2 text-center max-w-md">
            {error}
          </Text>
          <Button variant="outline" className="mt-6" onClick={onRetry}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Unread-only toggle switch (§4.7 "Toggle switch: 26x15, thumb 11px"),
 * matching Events.dc.html:46-49. A real checkbox-role button (not the
 * mockup's decorative span) so it is keyboard-operable, per §7.
 */
function UnreadToggle({ checked, onChange }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-[7px] text-xs text-text-secondary">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label="Unread only"
        onClick={onChange}
        className={cn(
          "relative inline-block h-[15px] w-[26px] rounded-full border-none transition-colors duration-[120ms] ease-out",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
          checked ? "bg-accent-tint" : "bg-border"
        )}
      >
        <span
          className={cn(
            "absolute top-[2px] h-[11px] w-[11px] rounded-full transition-colors duration-[120ms] ease-out",
            checked ? "left-[13px] bg-accent" : "left-[2px] bg-text-faint"
          )}
        />
      </button>
      Unread only
    </label>
  );
}

/**
 * Single event row — compact table row per Events.dc.html:65-78. Unread rows
 * get a faint accent tint background + 600-weight text (never color alone —
 * the unread accent DOT is the primary always-visible signal, per §7 "Never
 * color-only: unread carries dot + weight").
 */
function EventRow({ event }) {
  const category = getEventCategory(event.eventType);
  const details = getEventDetails(event);
  const severity = event.severity || "muted";
  const squareClass = SEVERITY_SQUARE_CLASS[severity] || SEVERITY_SQUARE_CLASS.muted;

  return (
    <TableRow className={event.read ? undefined : "bg-accent-tint/40"}>
      <TableCell className="whitespace-nowrap py-2 px-[18px] font-mono text-[11px] text-text-faint">
        {formatRelativeTime(event.timestamp)}
      </TableCell>
      <TableCell className="py-2">
        <div className="flex items-center gap-2">
          <span
            className={`h-[7px] w-[7px] shrink-0 rounded-sm ${squareClass}`}
            aria-hidden="true"
          />
          <span
            className={
              event.read
                ? "truncate text-text-secondary"
                : "truncate font-semibold text-foreground"
            }
          >
            {formatDescription(event)}
          </span>
          {!event.read && (
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
              title="Unread"
              aria-label="Unread"
            />
          )}
        </div>
      </TableCell>
      <TableCell className="py-2">
        <Badge variant="secondary" size="sm">
          {category}
        </Badge>
      </TableCell>
      <TableCell className="py-2 px-[18px] font-mono text-[11px] text-text-faint">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help truncate">{details}</span>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{formatFullTimestamp(event.timestamp)}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
    </TableRow>
  );
}

/**
 * Events log table — §4.6 "log table (Time | Event+severity | Category |
 * Details)" (Events.dc.html:54-81).
 */
function EventsTable({ events, hasMore, isLoadingMore, onLoadMore }) {
  return (
    <Card className="overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[110px] px-[18px]">Time</TableHead>
            <TableHead>Event</TableHead>
            <TableHead className="w-[120px]">Category</TableHead>
            <TableHead className="px-[18px]">Details</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event) => (
            <EventRow key={event.logId} event={event} />
          ))}
        </TableBody>
      </Table>
      {hasMore && (
        <div className="flex justify-center border-t border-hairline p-2.5">
          <Button variant="outline" size="sm" onClick={onLoadMore} disabled={isLoadingMore}>
            {isLoadingMore ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
                Loading...
              </>
            ) : (
              "Load more"
            )}
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * Events Page Component
 *
 * Displays all portfolio activity events in chronological order. Features:
 * category filter pills, unread-only toggle, mark-all-as-read, manual
 * refresh, relative timestamps (full timestamp on hover), pagination.
 */
export function Events() {
  const {
    events,
    loading,
    error,
    hasMore,
    unreadCount,
    filters,
    setFilters,
    loadMore,
    refresh,
    markAllAsRead,
  } = useEvents();

  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [isMarkingRead, setIsMarkingRead] = React.useState(false);

  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      setIsRefreshing(false);
    }
  }, [refresh]);

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    try {
      await loadMore();
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleMarkAllAsRead = React.useCallback(async () => {
    setIsMarkingRead(true);
    try {
      await markAllAsRead();
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    } finally {
      setIsMarkingRead(false);
    }
  }, [markAllAsRead]);

  // Filter out PORTFOLIO_REFRESHED events - they clutter the list. Instead,
  // the last refresh time is shown in the toolbar meta line (carried over
  // verbatim from the pre-restyle implementation).
  const filteredEvents = React.useMemo(() => {
    return events.filter((event) => event.eventType !== "PORTFOLIO_REFRESHED");
  }, [events]);

  const lastRefreshTime = React.useMemo(() => {
    const refreshEvent = events.find((event) => event.eventType === "PORTFOLIO_REFRESHED");
    return refreshEvent?.timestamp || null;
  }, [events]);

  const activeCategory = filters.category || "all";

  const secondaryActions = (
    <>
      {unreadCount > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleMarkAllAsRead}
          disabled={isMarkingRead}
          aria-label="Mark all read"
        >
          <CheckCheck className="h-3.5 w-3.5 sm:mr-1.5" />
          <span className="hidden sm:inline">Mark all read</span>
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={handleRefresh}
        disabled={isRefreshing}
        aria-label="Refresh"
      >
        <RefreshCw className={`h-3.5 w-3.5 sm:mr-1.5 ${isRefreshing ? "animate-spin motion-reduce:animate-none" : ""}`} />
        <span className="hidden sm:inline">Refresh</span>
      </Button>
    </>
  );

  // Loading state
  if (loading && events.length === 0) {
    return (
      <AppLayout title="Activity" secondaryActions={secondaryActions}>
        <LoadingState />
      </AppLayout>
    );
  }

  // Error state
  if (error && events.length === 0) {
    return (
      <AppLayout title="Activity" secondaryActions={secondaryActions}>
        <ErrorState error={error} onRetry={handleRefresh} />
      </AppLayout>
    );
  }

  // Empty state (no events at all, unfiltered)
  if (filteredEvents.length === 0 && events.length === 0 && activeCategory === "all" && !filters.unreadOnly) {
    return (
      <AppLayout title="Activity" secondaryActions={secondaryActions}>
        <EmptyState
          icon={Activity}
          title="No events yet"
          description="Your portfolio activity will appear here as you make changes."
          primaryAction={{
            label: "Go to Dashboard",
            href: "/",
          }}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Activity" secondaryActions={secondaryActions}>
      <div className="mx-auto flex max-w-[1240px] flex-col gap-3.5">
        {/* Filter row: category pills + unread toggle + meta (Events.dc.html:37-51) */}
        <div className="flex flex-wrap items-center gap-2">
          <FilterPillGroup>
            {CATEGORY_OPTIONS.map((option) => (
              <FilterPill
                key={option.value}
                active={activeCategory === option.value}
                onClick={() =>
                  setFilters({ category: option.value === "all" ? null : option.value })
                }
              >
                {option.label}
              </FilterPill>
            ))}
          </FilterPillGroup>

          <UnreadToggle
            checked={!!filters.unreadOnly}
            onChange={() => setFilters({ unreadOnly: !filters.unreadOnly })}
          />

          <MonoText size="xs" className="ml-auto text-text-faint">
            {filteredEvents.length} {filteredEvents.length === 1 ? "event" : "events"}
            {lastRefreshTime && ` · refreshed ${formatRelativeTime(lastRefreshTime)}`}
          </MonoText>
        </div>

        {filteredEvents.length > 0 ? (
          <EventsTable
            events={filteredEvents}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={handleLoadMore}
          />
        ) : (
          <Card>
            <CardContent className="py-8">
              <div className="text-center text-muted-foreground">
                <RefreshCw className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <Text variant="secondary">No events match the current filters</Text>
              </div>
            </CardContent>
          </Card>
        )}

        {!hasMore && filteredEvents.length > 0 && (
          <div className="py-2 text-center">
            <Text variant="secondary" size="sm">
              You have reached the end of your activity history
            </Text>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default Events;
